<template>
    <div class="dr">
        <!-- DATE NAV -->
        <div v-if="!noDate" class="date-nav-bar">
            <button type="button" class="date-nav-arrow" @click="loadRoutineDay(false)">
                <v-icon size="14">mdi-chevron-left</v-icon>
            </button>
            <span class="date-nav-lbl">{{ dateString }}</span>
            <button type="button" class="date-nav-arrow" @click="loadRoutineDay(true)">
                <v-icon size="14">mdi-chevron-right</v-icon>
            </button>
        </div>

        <div v-if="grouped" class="date-nav-bar">
            <button type="button" class="date-nav-arrow" @click="$emit('prev')">
                <v-icon size="14">mdi-chevron-left</v-icon>
            </button>
            <span class="date-nav-lbl">{{ currentRoutine && currentRoutine.name }}</span>
            <button type="button" class="date-nav-arrow" @click="$emit('next')">
                <v-icon size="14">mdi-chevron-right</v-icon>
            </button>
        </div>

        <template v-if="hasData">
            <!-- METRICS -->
            <div class="dr-metrics">
                <div class="dr-metric">
                    <div class="dr-metric-val">{{ stats.sets }}</div>
                    <div class="dr-metric-lbl">{{ $tc('exercises.sets', 2) }}</div>
                    <div v-if="seriesTrend" class="dr-metric-trend" :class="seriesTrend.up ? 'up' : 'down'">
                        <v-icon size="11" :color="seriesTrend.up ? 'success' : 'error'">
                            {{ seriesTrend.up ? 'mdi-menu-up' : 'mdi-menu-down' }}
                        </v-icon>{{ seriesTrend.pct }}%
                    </div>
                </div>
                <div class="dr-metric">
                    <div class="dr-metric-val">{{ stats.reps }}</div>
                    <div class="dr-metric-lbl">{{ $tc('exercises.reps', 2) }}</div>
                    <div v-if="repsTrend" class="dr-metric-trend" :class="repsTrend.up ? 'up' : 'down'">
                        <v-icon size="11" :color="repsTrend.up ? 'success' : 'error'">
                            {{ repsTrend.up ? 'mdi-menu-up' : 'mdi-menu-down' }}
                        </v-icon>{{ repsTrend.pct }}%
                    </div>
                </div>
                <div class="dr-metric">
                    <div class="dr-metric-val">{{ formatVolume(stats.volume) }}</div>
                    <div class="dr-metric-lbl">{{ $t('statistics.volume') }}</div>
                    <div v-if="volumeTrend" class="dr-metric-trend" :class="volumeTrend.up ? 'up' : 'down'">
                        <v-icon size="11" :color="volumeTrend.up ? 'success' : 'error'">
                            {{ volumeTrend.up ? 'mdi-menu-up' : 'mdi-menu-down' }}
                        </v-icon>{{ volumeTrend.pct }}%
                    </div>
                </div>
            </div>

            <!-- CARGA PLANIFICADA -->
            <div class="dr-section">
                <div class="dr-section-lbl">{{ $t('statistics.planned_load') }}</div>
                <div class="dr-carga-main">
                    <div class="dr-carga-val">{{ formatVolume(stats.volume) }}</div>
                    <div v-if="volumeTrend" class="dr-carga-vs">
                        <span class="dr-carga-badge" :class="volumeTrend.up ? 'up' : 'down'">
                            {{ volumeTrend.up ? '▲' : '▼' }} {{ volumeTrend.pct }}%
                        </span>
                        <span class="dr-carga-hint">{{ $t('statistics.vs_last_week') }}</span>
                    </div>
                </div>
                <div class="dr-carga-bar-wrap">
                    <div class="dr-carga-bar-track">
                        <div class="dr-carga-bar-fill" :style="{ width: loadFillPct + '%' }" />
                        <div v-if="prevStats.volume > 0" class="dr-carga-bar-marker" :style="{ left: prevMarkerPct + '%' }" />
                    </div>
                    <div class="dr-carga-bar-labels">
                        <span>0</span>
                        <span v-if="prevStats.volume > 0">{{ $t('statistics.last_week') }}: {{ formatVolume(prevStats.volume) }}</span>
                        <span>{{ formatVolume(maxLoad) }}</span>
                    </div>
                </div>
            </div>

            <!-- DISTRIBUCION -->
            <div class="dr-section">
                <div class="dr-section-lbl">{{ $t('statistics.distribution') }}</div>
                <div class="donut-switch">
                    <button type="button" class="donut-sw-btn" :class="{ active: donutMode === 'tipo' }"
                        @click="donutMode = 'tipo'">{{ $t('exercises.type') }}</button>
                    <button type="button" class="donut-sw-btn" :class="{ active: donutMode === 'categoria' }"
                        @click="donutMode = 'categoria'">{{ $t('common.category') }}</button>
                </div>
                <template v-if="distActive.length">
                    <div class="dr-donut-wrap">
                        <doughnut-chart :labels="chartLabels" :datasets="chartDatasets" :styles="donutStyles" />
                        <div class="donut-center">
                            <div class="donut-center-val">{{ donutTotal }}</div>
                            <div class="donut-center-lbl">{{ $tc('exercises.sets', 2) }}</div>
                        </div>
                    </div>
                    <div class="dr-legend">
                        <div v-for="(l, i) in legend" :key="i" class="dr-legend-item">
                            <span class="dr-legend-dot" :style="{ background: l.color }" />
                            <span class="dr-legend-name">{{ l.name }}</span>
                            <span class="dr-legend-pct">{{ l.pct }}%</span>
                        </div>
                    </div>
                </template>
                <div v-else class="dr-empty">{{ $t('common.without_data') }}</div>
            </div>

            <!-- VOLUMEN POR BLOQUE -->
            <div class="dr-section" v-if="stats.perBlock.length">
                <div class="dr-section-lbl">{{ $t('statistics.volume_by_block') }}</div>
                <div class="dr-bars">
                    <div v-for="(b, i) in stats.perBlock" :key="i" class="dr-bar-row">
                        <span class="dr-bar-lbl">{{ `${$tc('common.blocks', 1).charAt(0)}${i + 1}` }}</span>
                        <div class="dr-bar-track">
                            <div class="dr-bar-fill" :style="{ width: (b.volume / maxBlockVol * 100) + '%', background: b.color }" />
                        </div>
                        <span class="dr-bar-num">{{ formatVolume(b.volume) }}</span>
                    </div>
                </div>
            </div>
        </template>

        <div v-else class="dr-empty-full">
            <v-icon size="34" color="txtMuted">mdi-chart-donut</v-icon>
            <span>{{ $t('common.without_data') }}</span>
        </div>
    </div>
</template>

<script>
import { getMergedBlocks } from '../../../utils/Exercises';
import { db } from '../../../utils/Firebase';
import { getBlockColor } from '../../../utils/BlockColors';
import { debounce } from '@/utils/Common';
import DoughnutChart from '../../common/DoughnutChart.vue';

export default {
    name: 'AssignedRoutineCounters',
    components: { DoughnutChart },
    props: {
        routine: { type: [Object, Array], default: null },
        currentSelection: { type: Object, default: null },
        collection: { type: String, default: null },
        coachId: { type: String, default: null },
        routineNumber: { type: Number, default: 1 },
        defaultDate: { type: String, default: null },
        noDate: { type: Boolean, default: false },
        grouped: { type: Boolean, default: false },
        isCircuit: { type: Boolean, default: false },
    },
    data() {
        return {
            currentRoutine: this.routine,
            date: this.defaultDate,
            donutMode: 'tipo',
            stats: { sets: 0, reps: 0, volume: 0, tipo: [], categoria: [], perBlock: [] },
            prevStats: { sets: 0, reps: 0, volume: 0 },
        }
    },
    computed: {
        dateString(vm = this) {
            return vm.$moment(vm.date).format('DD-MM-YYYY');
        },
        hasData(vm = this) {
            return vm.stats.sets > 0;
        },
        hasBlockVolume(vm = this) {
            return vm.stats.perBlock.some(b => b.volume > 0);
        },
        distActive(vm = this) {
            return vm.donutMode === 'tipo' ? vm.stats.tipo : vm.stats.categoria;
        },
        donutTotal(vm = this) {
            return vm.distActive.reduce((a, d) => a + d.value, 0);
        },
        chartLabels(vm = this) {
            return vm.distActive.map(d => d.name);
        },
        chartDatasets(vm = this) {
            return [{
                data: vm.distActive.map(d => d.value),
                backgroundColor: vm.distActive.map(d => d.color),
                borderWidth: 0,
            }];
        },
        legend(vm = this) {
            const total = vm.donutTotal;
            return vm.distActive.map(d => ({
                name: d.name,
                color: d.color,
                pct: total ? Math.round(d.value / total * 100) : 0,
            }));
        },
        routineKey(vm = this) {
            return vm.currentRoutine?.rawDate?.full || vm.date || null;
        },
        seriesTrend(vm = this) {
            return vm.trend(vm.stats.sets, vm.prevStats.sets);
        },
        repsTrend(vm = this) {
            return vm.trend(vm.stats.reps, vm.prevStats.reps);
        },
        volumeTrend(vm = this) {
            return vm.trend(vm.stats.volume, vm.prevStats.volume);
        },
        maxLoad(vm = this) {
            return Math.max(vm.stats.volume, vm.prevStats.volume, 1) * 1.25;
        },
        loadFillPct(vm = this) {
            return Math.min(100, vm.stats.volume / vm.maxLoad * 100);
        },
        prevMarkerPct(vm = this) {
            return Math.min(100, vm.prevStats.volume / vm.maxLoad * 100);
        },
        maxBlockVol(vm = this) {
            return Math.max(...vm.stats.perBlock.map(b => b.volume), 1);
        },
        donutStyles() {
            return { position: 'relative', height: '100%', width: '100%' };
        },
    },
    methods: {
        trend(current, prev) {
            if (!prev) return null;
            const pct = Math.round((current - prev) / prev * 100);
            return { pct: Math.abs(pct), up: pct >= 0 };
        },
        formatVolume(kg) {
            if (!kg) return '0';
            return kg >= 1000 ? `${(kg / 1000).toFixed(1).replace('.0', '')}t` : `${Math.round(kg)}kg`;
        },
        categoryName(key) {
            const c = this.CATEGORY;
            const map = {
                [c.STRONG]: 'strong', [c.SPEED]: 'speed', [c.MOBILITY]: 'mobility',
                [c.STABILITY]: 'stability', [c.RESISTANCE]: 'resistance',
            };
            return this.$t(`objectives.${map[key] || 'strong'}`);
        },
        categoryColor(key) {
            const c = this.CATEGORY;
            const map = {
                [c.STRONG]: '#339999', [c.SPEED]: '#FF9933', [c.MOBILITY]: '#66CCCC',
                [c.STABILITY]: '#CCFF33', [c.RESISTANCE]: '#009966',
            };
            return map[key] || '#66CC99';
        },
        takeValueFromSet(set, attribute) {
            let value = set.data?.find(data => data.short_name === `short_${attribute}`);
            return value ? Number(value.value || 0) : 0;
        },
        computeStats(blocks) {
            const vm = this;
            let sets = 0, reps = 0, volume = 0;
            const catMap = {};
            const typeMap = {};
            const perBlock = [];

            blocks.forEach((block, bi) => {
                let bVol = 0;
                const isCircuit = block.isCircuit || false;
                const rounds = Number(block.sets) || 0;
                block.exercises.forEach(exercise => {
                    if (exercise.type === 'rest') return;
                    const exCircuit = isCircuit || exercise.isCircuit;
                    let setCount = 0;
                    if (exCircuit) {
                        setCount = rounds;
                        if ((exercise.config?.type || 'reps') === 'reps') {
                            reps += rounds * (Number(exercise.config?.value) || 0);
                        }
                    } else {
                        const exSets = exercise.config?.sets || [];
                        setCount = exSets.length;
                        exSets.forEach(set => {
                            const r = vm.takeValueFromSet(set, 'reps') || 0;
                            const w = vm.takeValueFromSet(set, 'weight') || 0;
                            reps += r;
                            volume += r * w;
                            bVol += r * w;
                        });
                    }
                    sets += setCount;

                    const catKey = vm.categoryKey(exercise.categories?.[0]);
                    if (catKey) {
                        if (!catMap[catKey]) catMap[catKey] = { name: vm.categoryName(catKey), value: 0, color: vm.categoryColor(catKey) };
                        catMap[catKey].value += setCount;
                    }

                    const typeName = exercise.types?.[0];
                    if (typeName) typeMap[typeName] = (typeMap[typeName] || 0) + setCount;
                });
                perBlock.push({ name: vm.blockNameLabel(block.name), volume: bVol, color: getBlockColor(bi) });
            });

            const tipo = Object.values(catMap).filter(d => d.value > 0).sort((a, b) => b.value - a.value);
            const categoria = Object.entries(typeMap)
                .map(([name, value]) => ({ name, value }))
                .filter(d => d.value > 0)
                .sort((a, b) => b.value - a.value)
                .map((d, i) => ({ ...d, color: getBlockColor(i) }));

            return { sets, reps, volume, tipo, categoria, perBlock };
        },
        async loadStats() {
            const vm = this;
            const blocks = await getMergedBlocks(vm.currentRoutine?.blocks || []);
            vm.stats = vm.computeStats(blocks);
        },
        debouncedLoadStats: debounce(function () {
            this.loadStats();
        }, 300),
        async loadPrevStats() {
            const vm = this;
            const fromDate = vm.currentRoutine?.rawDate?.full || vm.date;
            const query = vm.scopedQuery();
            if (!query || !fromDate) {
                vm.prevStats = { sets: 0, reps: 0, volume: 0 };
                return;
            }
            const ref = await query
                .where('routineNumber', '==', vm.currentRoutine?.routineNumber || vm.routineNumber)
                .where('rawDate.full', '<', fromDate)
                .orderBy('rawDate.full', 'desc')
                .limit(1)
                .get();

            if (ref.docs.length) {
                const blocks = await getMergedBlocks(ref.docs[0].data().blocks || []);
                const s = vm.computeStats(blocks);
                vm.prevStats = { sets: s.sets, reps: s.reps, volume: s.volume };
            } else {
                vm.prevStats = { sets: 0, reps: 0, volume: 0 };
            }
        },
        scopedQuery() {
            const vm = this;
            if (!vm.collection) return null;
            const query = db.collection(vm.collection);
            if (vm.collection === 'assignedRoutines') {
                const studentId = vm.currentRoutine?.studentId || vm.currentSelection?.objectID || null;
                return studentId ? query.where('studentId', '==', studentId) : null;
            }
            const groupId = vm.currentRoutine?.groupId || vm.currentSelection?.id || null;
            const teamId = vm.currentRoutine?.teamId || vm.currentSelection?.teamId || null;
            return groupId ? query.where('groupId', '==', groupId).where('teamId', '==', teamId) : null;
        },
        async loadRoutineDay(next = false) {
            const vm = this;
            const fromDate = vm.currentRoutine?.rawDate?.full || vm.date;
            const query = vm.scopedQuery();
            if (!query || !fromDate) return;

            const newDate = vm.$moment(vm.date).add(next ? 7 : -7, 'days').format('YYYY-MM-DD');
            const ref = await query
                .where('routineNumber', '==', vm.currentRoutine?.routineNumber || vm.routineNumber)
                .where('rawDate.full', next ? '>' : '<', fromDate)
                .orderBy('rawDate.full', next ? 'asc' : 'desc')
                .limit(1)
                .get();

            vm.date = newDate;
            vm.currentRoutine = newDate === vm.defaultDate
                ? vm.routine
                : (ref.docs.length ? { ...ref.docs[0].data(), id: ref.docs[0].id } : null);
            vm.loadStats();
        },
    },
    watch: {
        routine: {
            handler(val) {
                if (this.date !== this.defaultDate) return;
                this.currentRoutine = val;
                this.debouncedLoadStats();
            },
            immediate: true,
            deep: true,
        },
        routineKey: {
            handler() {
                this.loadPrevStats();
            },
            immediate: true,
        },
        defaultDate(val) {
            this.date = val;
            this.currentRoutine = this.routine;
            this.debouncedLoadStats();
        },
    },
}
</script>

<style scoped>
.dr {
    display: flex;
    flex-direction: column;
}

.date-nav-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 41px;
    padding: 0 12px;
    background: var(--v-surface2-base);
    border-bottom: 1px solid var(--v-outline-base);
}

.date-nav-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    color: var(--v-txtSecondary-base);
    cursor: pointer;
    transition: border-color .15s, color .15s;
}

.date-nav-arrow:hover {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.date-nav-lbl {
    font-size: 12px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.dr-metrics {
    display: flex;
    border-bottom: 1px solid var(--v-outline-base);
}

.dr-metric {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 12px 6px;
    border-right: 1px solid var(--v-outline-base);
}

.dr-metric:last-child {
    border-right: none;
}

.dr-metric-val {
    font-size: 18px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.dr-metric-lbl {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: var(--v-txtMuted-base);
}

.dr-metric-trend {
    display: flex;
    align-items: center;
    font-size: 10px;
    font-weight: 700;
}

.dr-metric-trend.up {
    color: var(--v-success-base);
}

.dr-metric-trend.down {
    color: var(--v-error-base);
}

.dr-section {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 12px;
    border-bottom: 1px solid var(--v-outline-base);
}

.dr-section-lbl {
    font-size: 9px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--v-txtMuted-base);
}

.dr-carga-main {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
}

.dr-carga-val {
    font-size: 26px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
}

.dr-carga-vs {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
}

.dr-carga-badge {
    font-size: 11px;
    font-weight: 700;
}

.dr-carga-badge.up {
    color: var(--v-success-base);
}

.dr-carga-badge.down {
    color: var(--v-error-base);
}

.dr-carga-hint {
    font-size: 9px;
    color: var(--v-txtMuted-base);
}

.dr-carga-bar-track {
    position: relative;
    height: 8px;
    border-radius: 4px;
    background: var(--v-surface2-base);
    overflow: hidden;
}

.dr-carga-bar-fill {
    height: 100%;
    border-radius: 4px;
    background: linear-gradient(90deg, var(--v-primary-base), var(--v-accent-base));
    transition: width .6s cubic-bezier(.4, 0, .2, 1);
}

.dr-carga-bar-marker {
    position: absolute;
    top: -2px;
    width: 2px;
    height: 12px;
    background: var(--v-txtSecondary-base);
}

.dr-carga-bar-labels {
    display: flex;
    justify-content: space-between;
    gap: 6px;
    margin-top: 4px;
    font-size: 9px;
    color: var(--v-txtMuted-base);
}

.donut-switch {
    display: flex;
    gap: 2px;
    padding: 3px;
    border-radius: 8px;
    background: var(--v-surface2-base);
}

.donut-sw-btn {
    flex: 1;
    padding: 5px 4px;
    border-radius: 6px;
    background: transparent;
    color: var(--v-txtMuted-base);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: color .15s, background .15s;
}

.donut-sw-btn.active {
    background: var(--v-surface-base);
    color: var(--v-txtPrimary-base);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
}

.dr-donut-wrap {
    position: relative;
    height: 140px;
    padding: 4px 0;
}

.donut-center {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
}

.donut-center-val {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    line-height: 1;
}

.donut-center-lbl {
    font-size: 10px;
    color: var(--v-txtMuted-base);
}

.dr-legend {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.dr-legend-item {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 11px;
    color: var(--v-txtSecondary-base);
}

.dr-legend-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.dr-legend-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dr-legend-pct {
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.dr-bars {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.dr-bar-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.dr-bar-lbl {
    width: 24px;
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 700;
    color: var(--v-txtMuted-base);
}

.dr-bar-track {
    flex: 1;
    height: 8px;
    border-radius: 4px;
    background: var(--v-surface2-base);
    overflow: hidden;
}

.dr-bar-fill {
    height: 100%;
    border-radius: 4px;
    transition: width .6s cubic-bezier(.4, 0, .2, 1);
}

.dr-bar-num {
    width: 42px;
    flex-shrink: 0;
    text-align: right;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
}

.dr-empty {
    padding: 20px 8px;
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.dr-empty-full {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 48px 16px;
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
}
</style>
